import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { money } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useToast } from '../../ui/feedback';
import { useInvestorScope } from '../../store/selectors';
import { StatusChip } from '../../ui/primitives';
import { Card } from '../common';
import type { ApplyForm } from './applyTypes';

export default function ApplyComplete({
  applicationId,
  form,
}: {
  applicationId: string;
  form: ApplyForm;
}) {
  const scope = useInvestorScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [paid, setPaid] = useState(false);
  const application = scope.applications.find((item) => item.id === applicationId);
  const fund = scope.funds.find((item) => item.id === form.fundId);
  const reference = application?.reference ?? 'your application reference';
  return (
    <Card sx={{ maxWidth: 640, mx: 'auto', textAlign: 'center' }}>
      <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
      <Typography variant="h5" sx={{ mt: 1 }}>
        {application?.status === 'ISSUED' ? 'Units issued' : 'Application submitted'}
      </Typography>
      {application && (
        <Box sx={{ mt: 1 }}>
          <StatusChip status={application.status} />
        </Box>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Thanks. Your application {reference} for {money(Number(form.amount) || 0)} in {fund?.name}{' '}
        is with our registry team. Units are issued once your payment arrives and the application is
        approved.
      </Typography>
      <Divider sx={{ my: 2.5 }} />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Pay your application monies
      </Typography>
      <Stack
        spacing={0.75}
        sx={{ textAlign: 'left', bgcolor: 'background.default', p: 2, borderRadius: '6px' }}
      >
        {[
          ['Amount', money(Number(form.amount) || 0)],
          ['BSB', fund?.bank.bsb ?? ''],
          ['Account', fund?.bank.accountNumber ?? ''],
          ['Account name', fund?.bank.accountName ?? ''],
          ['Reference (important)', reference],
        ].map(([label, value]) => (
          <Stack key={label} direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {value}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          disabled={
            paid ||
            !application ||
            application.checklist.funds === 'DONE' ||
            application.status === 'ISSUED'
          }
          onClick={() => {
            if (!application) return;
            dispatch({ type: 'INVESTOR_PAY_APPLICATION', applicationId: application.id });
            setPaid(true);
            toast(
              'Simulated: EFT sent. It appears in the manager’s bank feed as a suggested match.',
            );
          }}
        >
          {paid ? 'Payment sent' : 'Simulate EFT payment'}
        </Button>
        <Button component={RouterLink} to="/investor" variant="outlined">
          Back to home
        </Button>
        <Button
          component={RouterLink}
          to={application ? `/manager/applications/${application.id}` : '/manager/applications'}
          variant="outlined"
        >
          See it in the manager view
        </Button>
      </Box>
    </Card>
  );
}
